import React from 'react';
import { createPortal } from 'react-dom';

export enum ConfirmationType {
  SUCCESS = 'success',
  WARNING = 'warning',
  DANGER = 'danger',
  INFO = 'info'
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  type?: ConfirmationType;
  confirmButtonText?: string;
  cancelButtonText?: string;
  customIcon?: React.ReactNode;
}

const getTypeStyles = (type: ConfirmationType): {
  iconBg: string;
  iconColor: string;
  buttonBg: string;
  accentBar: string;
  icon: React.ReactNode;
} => {
  switch (type) {
    case ConfirmationType.SUCCESS:
      return {
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        buttonBg: 'bg-emerald-600 hover:bg-emerald-700',
        accentBar: 'bg-emerald-500',
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
        )
      };
    case ConfirmationType.WARNING:
      return {
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        buttonBg: 'bg-amber-600 hover:bg-amber-700',
        accentBar: 'bg-amber-500',
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.834-1.964-.834-2.732 0L4.732 16c-.77.833.192 3 1.732 3z" />
        )
      };
    case ConfirmationType.DANGER:
      return {
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        buttonBg: 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800',
        accentBar: 'bg-red-500',
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-.834-1.964-.834-2.732 0L4.732 16c-.77.833.192 3 1.732 3z" />
        )
      };
    case ConfirmationType.INFO:
      return {
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        buttonBg: 'bg-blue-600 hover:bg-blue-700',
        accentBar: 'bg-blue-500',
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        )
      };
    default:
      return getTypeStyles(ConfirmationType.INFO);
  }
};

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = ConfirmationType.INFO,
  confirmButtonText = 'Confirm',
  cancelButtonText = 'Cancel',
  customIcon
}) => {
  if (!isOpen) return null;

  const styles = getTypeStyles(type);

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-xl w-full mx-auto shadow-2xl border border-slate-200/60 overflow-hidden">
        {/* Colored top accent bar */}
        <div className={`h-1 ${styles.accentBar}`} />

        <div className="p-5">
          {/* Icon + Title */}
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center shrink-0 mt-0.5">
              {customIcon || (
                <svg className={`w-4.5 h-4.5 ${styles.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {styles.icon}
                </svg>
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800 tracking-wide">{title}</h3>
              <p className="text-[11px] text-slate-400 mt-0.5 font-medium uppercase tracking-wider">
                {type === ConfirmationType.WARNING && 'Requires attention'}
                {type === ConfirmationType.DANGER && 'Destructive action'}
                {type === ConfirmationType.SUCCESS && 'Confirm action'}
                {type === ConfirmationType.INFO && 'Information'}
              </p>
            </div>
          </div>

          {/* Message */}
          <div className="ml-12 mb-5 text-xs 3xl:text-sm text-slate-600 leading-relaxed">
            {message}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100 -mx-5 mb-4" />

          {/* Actions */}
          <div className="flex gap-2.5 justify-end">
            <button
              onClick={onClose}
              className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors duration-150"
            >
              {cancelButtonText}
            </button>
            <button
              onClick={onConfirm}
              className={`px-3.5 py-1.5 text-xs font-medium text-white ${styles.buttonBg} rounded-lg shadow-sm transition-all duration-150`}
            >
              {confirmButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
